import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getParentSessionDetail, getSessionFlags } from '../../services/api';
import SignOutButton from '../../components/SignOutButton';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  surfaceSubtle: '#F1F5F9',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
  skeletonBase: '#F1F5F9',
  skeletonHighlight: '#E2E8F0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// ─── Brand mark (matches Dashboard) ───────────────────────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0, 102, 255, 0.3)',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT,
        fontSize: 17,
        fontWeight: 700,
        color: C.text,
        letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Amber palette for the flagged-message highlight. Kept local so the
// indicator is visually distinct from the existing blue accent and the red
// destructive tones used elsewhere on the parent dashboard.
const FLAG_AMBER       = '#D97706';
const FLAG_AMBER_SOFT  = '#FEF3C7';
const FLAG_AMBER_BORDER = '#FBBF24';

function MessageBubble({ message, flagged }) {
  const isChild = message.role === 'child';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: isChild ? 'flex-end' : 'flex-start',
      marginBottom: 16,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 5,
        paddingLeft: isChild ? 0 : 4,
        paddingRight: isChild ? 4 : 0,
      }}>
        <span style={{
          fontFamily: BODY_FONT,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isChild ? C.accentDark : C.textMuted,
        }}>{isChild ? 'Child' : 'Lantern'}</span>
        <span style={{ fontSize: 11, color: C.textSubtle }}>· {formatTime(message.createdAt)}</span>
        {message.inputMethod === 'voice' && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            color: C.accent,
            background: C.accentSoft,
            border: `1px solid ${C.accentBorder}`,
            borderRadius: 999,
            padding: '1px 7px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
              <rect x="3.5" y="1" width="3" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M2 5a3 3 0 0 0 6 0M5 8v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            voice
          </span>
        )}
        {flagged && (
          // Small shield chip in the message metadata row. The full amber
          // left border on the bubble below provides the at-a-glance visual
          // cue; this chip names the signal explicitly so a parent
          // scanning the transcript knows why the bubble is amber.
          <span
            aria-label="Safeguarding flag on this message"
            title="A safeguarding flag was raised on this message. See the Safeguarding screen to record your review."
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: FLAG_AMBER,
              background: FLAG_AMBER_SOFT,
              border: `1px solid ${FLAG_AMBER_BORDER}`,
              borderRadius: 999,
              padding: '1px 7px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2.5l8 3v5.5c0 4.5-3.3 8.3-8 9.5-4.7-1.2-8-5-8-9.5V5.5l8-3z"
                stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
            </svg>
            flagged
          </span>
        )}
      </div>
      <div style={{
        maxWidth: '78%',
        background: isChild ? C.accent : C.surfaceMuted,
        color: isChild ? '#fff' : C.text,
        // Amber left border replaces the regular border on flagged messages
        // so the highlight is visible against both bubble backgrounds (the
        // blue child bubble has no border by default; the muted Lantern
        // bubble does). 4px is thick enough to read from a scroll without
        // shouting at the parent on every routine session.
        border: flagged
          ? `1px solid ${FLAG_AMBER_BORDER}`
          : (isChild ? 'none' : `1px solid ${C.border}`),
        borderLeft: flagged ? `4px solid ${FLAG_AMBER}` : undefined,
        borderRadius: isChild ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        padding: '11px 15px',
        fontSize: 14,
        lineHeight: 1.55,
        fontFamily: BODY_FONT,
        boxShadow: flagged
          ? `0 1px 2px ${FLAG_AMBER}26`
          : (isChild ? '0 1px 2px rgba(0, 102, 255, 0.18)' : '0 1px 2px rgba(15, 23, 42, 0.04)'),
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        textWrap: 'pretty',
      }}>
        {message.content}
      </div>
    </div>
  );
}

function SkeletonBlock({ width, height, style }) {
  return (
    <div
      aria-hidden="true"
      style={{
        background: C.skeletonBase,
        backgroundImage: `linear-gradient(90deg, ${C.skeletonBase} 0%, ${C.skeletonHighlight} 50%, ${C.skeletonBase} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'sd-shimmer 1.4s ease-in-out infinite',
        borderRadius: 6,
        width,
        height,
        ...style,
      }}
    />
  );
}

function SessionDetailSkeleton() {
  const bubbles = [
    { side: 'right', width: '50%' },
    { side: 'left',  width: '72%' },
    { side: 'right', width: '38%' },
    { side: 'left',  width: '64%' },
  ];
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <SkeletonBlock width={260} height={28} style={{ marginBottom: 12 }} />
        <SkeletonBlock width={360} height={14} />
      </div>
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '24px 20px',
      }}>
        {bubbles.map((b, i) => (
          <div key={i} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: b.side === 'right' ? 'flex-end' : 'flex-start',
            marginBottom: 16,
          }}>
            <SkeletonBlock width={80} height={10} style={{ marginBottom: 6 }} />
            <SkeletonBlock width={b.width} height={52} style={{
              borderRadius: b.side === 'right' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            }}/>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main session detail ──────────────────────────────────────────────────────

export default function SessionDetail() {
  const { sessionId }     = useParams();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [flags, setFlags] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getParentSessionDetail(sessionId),
      // Flag fetch is best-effort — a 401 still bounces us out, but any
      // other failure (e.g. 404 for a session that has no flags returned
      // an empty list) shouldn't block the transcript itself rendering.
      getSessionFlags(sessionId).catch(() => ({ data: { flags: [] } })),
    ])
      .then(([detailRes, flagsRes]) => {
        if (cancelled) return;
        setData(detailRes.data);
        setFlags(flagsRes.data?.flags || []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) {
          navigate('/parent/login');
          return;
        }
        setError('Could not load session. It may belong to a different family.');
      });
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  const session = data?.session;
  const messages = data?.messages || [];
  const childId = session?.child_id;

  // Set of message ids that triggered a safeguarding flag — used to apply
  // the amber-bordered shield highlight in MessageBubble. Older flags from
  // pre-migration-014 auto-scans may have no message_id; they show up on
  // the session-level Safeguarding chip instead.
  const flaggedMessageIds = new Set(
    flags.map(f => f.message_id).filter(Boolean)
  );

  return (
    <>
      <style>{`
        @keyframes sd-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .sd-back:hover { background: ${C.surfaceMuted}; border-color: ${C.borderStrong}; }
        .sd-flag-card:hover { background: ${C.redSoft}; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
      }}>
        {/* Top nav */}
        <div style={{
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}>
          <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <LanternBrand />
            <SignOutButton />
          </div>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 56px' }}>

          {/* Back button */}
          {!error && (
            <button
              className="sd-back"
              onClick={() => childId ? navigate(`/parent/sessions/${childId}`) : navigate('/parent/dashboard')}
              style={{
                fontFamily: BODY_FONT,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 14px',
                background: '#fff',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 24,
                transition: 'background 160ms ease, border-color 160ms ease',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M11 7H3M7 3l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to sessions
            </button>
          )}

          {error && (
            <div style={{
              background: C.redSoft,
              border: `1px solid ${C.redBorder}`,
              borderRadius: 10,
              padding: '14px 18px',
              color: C.redText,
              fontSize: 14,
            }}>{error}</div>
          )}

          {!data && !error && <SessionDetailSkeleton />}

          {session && (
            <>
              {/* Session header */}
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 10,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: session.ended_at ? C.textSubtle : C.accent,
                  }}/>
                  {session.ended_at ? 'Completed session' : 'Active session'}
                </div>
                <h1 style={{
                  margin: '0 0 12px',
                  fontFamily: HEADING_FONT,
                  fontSize: 28,
                  fontWeight: 700,
                  color: C.text,
                  letterSpacing: '-0.025em',
                  lineHeight: 1.15,
                }}>
                  {session.subject} <span style={{ color: C.textSubtle, fontWeight: 600 }}>·</span> {session.mode}
                </h1>

                {/* Metadata row */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0,
                  alignItems: 'center',
                  fontSize: 13,
                  color: C.textMuted,
                }}>
                  {[
                    `Started ${formatDate(session.started_at)}`,
                    session.ended_at ? `Ended ${formatDate(session.ended_at)}` : null,
                    `${session.message_count} message${session.message_count !== 1 ? 's' : ''}`,
                  ].filter(Boolean).map((item, i, arr) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span>{item}</span>
                      {i < arr.length - 1 && (
                        <span style={{ color: C.textSubtle, margin: '0 10px' }}>·</span>
                      )}
                    </span>
                  ))}
                </div>

                {/* Safeguarding flag — clickable alert card */}
                {session.safeguarding_flags > 0 && (
                  <button
                    className="sd-flag-card"
                    onClick={() => navigate('/parent/safeguarding')}
                    style={{
                      marginTop: 16,
                      width: '100%',
                      background: '#fff',
                      border: `1px solid ${C.redBorder}`,
                      borderLeft: `4px solid ${C.red}`,
                      borderRadius: 10,
                      padding: '14px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      fontFamily: BODY_FONT,
                      transition: 'background 160ms ease',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: C.redSoft,
                      color: C.red,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1l7 13H1L8 1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                        <path d="M8 6v3.5M8 11.5v.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.redText }}>
                        {session.safeguarding_flags} safeguarding flag{session.safeguarding_flags !== 1 ? 's' : ''} in this session
                      </div>
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                        Tap to see the flagged message and record your review.
                      </div>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: C.redText, flexShrink: 0 }}>
                      <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* Conversation */}
              <div style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                padding: '24px 22px',
                minHeight: 240,
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              }}>
                {messages.length === 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    textAlign: 'center',
                  }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: C.surfaceMuted,
                      color: C.textSubtle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 12,
                    }}>
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <path d="M3 7c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2h-7l-4 3v-3H5c-1.1 0-2-.9-2-2V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>
                      No messages yet
                    </p>
                    <p style={{ color: C.textMuted, fontSize: 13, margin: 0 }}>
                      This session has no messages to review.
                    </p>
                  </div>
                ) : (
                  messages.map(m => (
                    <MessageBubble
                      key={m.id}
                      message={m}
                      flagged={flaggedMessageIds.has(m.id)}
                    />
                  ))
                )}
              </div>

              {/* Privacy footer */}
              <div style={{
                marginTop: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                color: C.textSubtle,
              }}>
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <rect x="3" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M5 6V4.5a2 2 0 0 1 4 0V6" stroke="currentColor" strokeWidth="1.4"/>
                </svg>
                Your family's conversations are encrypted and private.
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
